import { ApolloError } from '@apollo/client';
import { GraphQLFormattedError } from 'graphql';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	Accordion,
	Button,
	Callout,
	GraphQLErrorMessage,
	Heading3,
	Input,
	Paragraph,
	Spinner,
	tokens,
} from '@treams/treams-ui';

const PairRow = styled(Flex)<{ $unsaved?: boolean }>`
	align-items: stretch;
	margin: 4px 8px;
	border-radius: 6px;
	border: 1px solid
		${(props) => (props.$unsaved ? 'rgba(0, 122, 255, 0.25)' : 'rgba(0, 0, 0, 0.06)')};
	${(props) => props.$unsaved && `background-color: rgba(0, 122, 255, 0.04);`}
`;

const PairPanel = styled(Box)`
	flex: 1;
	padding: 10px 12px;
	min-width: 0;
`;

const ArrowIndicator = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	flex-shrink: 0;
	font-size: 18px;
	color: ${tokens.text.secondary};
`;

const ItemCard = styled.div`
	padding: 10px 12px;
	margin: 4px 8px;
	border-radius: 6px;
	border: 1px solid rgba(0, 0, 0, 0.06);
`;

const DragArea = styled.div`
	position: relative;
`;

const DraggableItem = styled(ItemCard)<{ $isDragging?: boolean; $canDrag?: boolean }>`
	${(props) => props.$canDrag && `cursor: grab;`}
	${(props) =>
		props.$isDragging &&
		`
		opacity: 0.5;
		outline: 2px solid #5856D6;
		outline-offset: -2px;
		cursor: grabbing;
	`}
	&:active {
		${(props) => props.$canDrag && `cursor: grabbing;`}
	}
`;

const DropTarget = styled(ItemCard)<{ $isHighlighted?: boolean }>`
	transition:
		outline 0.15s,
		background-color 0.15s;
	${(props) =>
		props.$isHighlighted &&
		`
		outline: 2px dashed #5856D6;
		outline-offset: -2px;
		background-color: rgba(88, 86, 214, 0.08);
	`}
`;

// --- Types ---

export interface MigrationItem {
	name: string;
	subtitle?: string;
}

export interface MatchedPair extends MigrationItem {
	id: string;
	currentRefId: string;
	suggestedRefId?: string;
	sourceName: string;
	sourceSubtitle?: string;
}

export interface NewRow extends MigrationItem {
	key: string;
	refId: string;
}

export interface UnmatchedRow extends MigrationItem {
	id: string;
	currentRefId: string;
}

export interface MigrationRecord {
	id: string;
	externalReferenceID?: string | null;
	matchKey: string;
	name: string;
	subtitle?: string;
}

export interface DryRunRecord {
	key: string;
	externalReferenceID: string;
	matchKey: string;
	name: string;
	subtitle?: string;
}

// --- Helpers ---

function normalizeForMatch(value: string): string {
	return value.trim().toLowerCase();
}

function getChangedReferences(
	references: Record<string, string>,
	baseline: Record<string, string>,
): Record<string, string> {
	const changed: Record<string, string> = {};
	for (const [id, newRef] of Object.entries(references)) {
		if (newRef !== (baseline[id] ?? '')) {
			changed[id] = newRef;
		}
	}
	return changed;
}

// --- Hook ---

export function useMigrationMatching(existing: MigrationRecord[], dryRun: DryRunRecord[]) {
	return useMemo(() => {
		const dryRunByRef = new Map<string, DryRunRecord>();
		const dryRunByKey = new Map<string, DryRunRecord>();
		for (const item of dryRun) {
			if (item.externalReferenceID) dryRunByRef.set(item.externalReferenceID, item);
			dryRunByKey.set(normalizeForMatch(item.matchKey), item);
		}

		const matchedDryRunIds = new Set<string>();
		const matchedList: MatchedPair[] = [];
		const unmatchedList: UnmatchedRow[] = [];

		for (const item of existing) {
			const matchByRef = item.externalReferenceID
				? dryRunByRef.get(item.externalReferenceID)
				: undefined;
			const matchByKey = dryRunByKey.get(normalizeForMatch(item.matchKey));
			const match = matchByRef ?? matchByKey;

			if (match && !matchedDryRunIds.has(match.externalReferenceID)) {
				matchedDryRunIds.add(match.externalReferenceID);
				matchedList.push({
					id: item.id,
					currentRefId: item.externalReferenceID ?? '',
					suggestedRefId: match.externalReferenceID,
					sourceName: match.name,
					sourceSubtitle: match.subtitle,
					name: item.name,
					subtitle: item.subtitle,
				});
			} else {
				unmatchedList.push({
					id: item.id,
					currentRefId: item.externalReferenceID ?? '',
					name: item.name,
					subtitle: item.subtitle,
				});
			}
		}

		const newList: NewRow[] = dryRun
			.filter((item) => !matchedDryRunIds.has(item.externalReferenceID))
			.map((item) => ({
				key: item.key,
				refId: item.externalReferenceID,
				name: item.name,
				subtitle: item.subtitle,
			}))
			.sort((a, b) => a.name.localeCompare(b.name));

		unmatchedList.sort((a, b) => a.name.localeCompare(b.name));

		return { matched: matchedList, unmatched: unmatchedList, newItems: newList };
	}, [existing, dryRun]);
}

function ItemLabel({ name, subtitle }: MigrationItem) {
	return (
		<>
			<Paragraph style={{ fontWeight: 500 }}>{name}</Paragraph>
			{subtitle && <Paragraph style={{ fontSize: 13, opacity: 0.7 }}>{subtitle}</Paragraph>}
		</>
	);
}

// --- Component ---

export interface MigrationSectionProps {
	title: string;
	loading: boolean;
	matched: MatchedPair[];
	newItems: NewRow[];
	unmatched: UnmatchedRow[];
	onSave: (id: string, externalReferenceID: string) => Promise<void>;
	onRefetch: () => Promise<unknown>;
	emptyMessage: string;
}

interface DragSource {
	sourceKey: string;
	startX: number;
	startY: number;
}

export default function MigrationSection({
	title,
	loading,
	matched,
	newItems,
	unmatched,
	onSave,
	onRefetch,
	emptyMessage,
}: MigrationSectionProps) {
	const { t } = useTranslation();
	const [references, setReferences] = useState<Record<string, string>>({});
	const [saving, setSaving] = useState(false);
	const [saveResult, setSaveResult] = useState<{
		success: number;
		failed: number;
		errors: GraphQLFormattedError[];
	} | null>(null);
	// Manual matching via drag
	const [manualMatches, setManualMatches] = useState<
		Array<{ newKey: string; unmatchedId: string }>
	>([]);
	const [dragSource, setDragSource] = useState<DragSource | null>(null);
	const [dragMouse, setDragMouse] = useState<{ x: number; y: number } | null>(null);
	const [dragHoverTarget, setDragHoverTarget] = useState<string | null>(null);

	const [searchNew, setSearchNew] = useState('');
	const [searchArchived, setSearchArchived] = useState('');

	const dragAreaRef = useRef<HTMLDivElement>(null);
	const hoveredTargetRef = useRef<string | null>(null);
	const newItemsRef = useRef(newItems);
	newItemsRef.current = newItems;

	// Derive manual matched pairs
	const manualNewKeys = useMemo(() => new Set(manualMatches.map((m) => m.newKey)), [manualMatches]);
	const manualUnmatchedIds = useMemo(
		() => new Set(manualMatches.map((m) => m.unmatchedId)),
		[manualMatches],
	);

	const manualMatchedPairs = useMemo(
		(): MatchedPair[] =>
			manualMatches.reduce<MatchedPair[]>((acc, m) => {
				const newItem = newItems.find((item) => item.key === m.newKey);
				const unmatchedItem = unmatched.find((row) => row.id === m.unmatchedId);
				if (newItem && unmatchedItem) {
					acc.push({
						id: unmatchedItem.id,
						currentRefId: unmatchedItem.currentRefId,
						suggestedRefId: newItem.refId,
						sourceName: newItem.name,
						sourceSubtitle: newItem.subtitle,
						name: unmatchedItem.name,
						subtitle: unmatchedItem.subtitle,
					});
				}
				return acc;
			}, []),
		[manualMatches, newItems, unmatched],
	);

	const allMatchedRaw = useMemo(
		() => [...matched, ...manualMatchedPairs],
		[matched, manualMatchedPairs],
	);

	const clearedMatchIds = useMemo(() => {
		const ids = new Set<string>();
		for (const pair of allMatchedRaw) {
			if (pair.id in references && references[pair.id] === '') {
				ids.add(pair.id);
			}
		}
		return ids;
	}, [allMatchedRaw, references]);

	const allMatched = useMemo(
		() => allMatchedRaw.filter((p) => !clearedMatchIds.has(p.id)),
		[allMatchedRaw, clearedMatchIds],
	);

	const clearedAsUnmatched = useMemo(
		(): UnmatchedRow[] =>
			allMatchedRaw
				.filter((p) => clearedMatchIds.has(p.id))
				.map((p) => ({
					id: p.id,
					currentRefId: p.currentRefId,
					name: p.name,
					subtitle: p.subtitle,
				})),
		[allMatchedRaw, clearedMatchIds],
	);

	const displayedNewItems = useMemo(
		() => newItems.filter((item) => !manualNewKeys.has(item.key)),
		[newItems, manualNewKeys],
	);
	const displayedUnmatched = useMemo(
		() => [...unmatched.filter((row) => !manualUnmatchedIds.has(row.id)), ...clearedAsUnmatched],
		[unmatched, manualUnmatchedIds, clearedAsUnmatched],
	);

	const matchesSearch = (item: MigrationItem, query: string) => {
		const q = query.toLowerCase();
		return (
			item.name.toLowerCase().includes(q) || (item.subtitle?.toLowerCase().includes(q) ?? false)
		);
	};

	const filteredNewItems = useMemo(() => {
		if (!searchNew) return displayedNewItems;
		return displayedNewItems.filter((item) => matchesSearch(item, searchNew));
	}, [displayedNewItems, searchNew]);

	const filteredUnmatched = useMemo(() => {
		if (!searchArchived) return displayedUnmatched;
		return displayedUnmatched.filter((row) => matchesSearch(row, searchArchived));
	}, [displayedUnmatched, searchArchived]);

	// Initialize references for matched items (auto + manual)
	useEffect(() => {
		setReferences((prev) => {
			const next = { ...prev };
			let changed = false;
			for (const pair of allMatched) {
				if (pair.suggestedRefId && !(pair.id in next)) {
					next[pair.id] = pair.suggestedRefId;
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [allMatched]);

	const currentRefMap = useMemo(() => {
		const map: Record<string, string> = {};
		for (const pair of allMatched) {
			map[pair.id] = pair.currentRefId;
		}
		for (const row of displayedUnmatched) {
			map[row.id] = row.currentRefId;
		}
		return map;
	}, [allMatched, displayedUnmatched]);

	const changedRefs = getChangedReferences(references, currentRefMap);
	const changedIds = new Set(Object.keys(changedRefs).filter((id) => !clearedMatchIds.has(id)));
	const changedCount = changedIds.size;
	const unsavedMatchedCount = allMatched.filter((p) => changedIds.has(p.id)).length;

	const handleSave = async () => {
		const changed = getChangedReferences(references, currentRefMap);
		const entries = Object.entries(changed).filter(([id]) => !clearedMatchIds.has(id));
		if (entries.length === 0) return;

		setSaving(true);
		setSaveResult(null);

		const results = await Promise.allSettled(entries.map(([id, newRef]) => onSave(id, newRef)));
		const success = results.filter((result) => result.status === 'fulfilled').length;
		const failed = results.length - success;

		const errors = results
			.filter((result) => result.status === 'rejected')
			.flatMap((result) => {
				if (result.reason instanceof ApolloError) {
					return result.reason.graphQLErrors;
				}
				return [];
			});

		setSaveResult({ success, failed, errors });
		setSaving(false);

		if (success > 0) {
			await onRefetch();
			setManualMatches([]);
		}
	};

	// --- Drag-to-link ---

	const handleDragStart = (e: React.MouseEvent, sourceKey: string) => {
		const rect = dragAreaRef.current?.getBoundingClientRect();
		if (!rect) return;
		const itemRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
		setDragSource({
			sourceKey,
			startX: itemRect.right - rect.left,
			startY: itemRect.top + itemRect.height / 2 - rect.top,
		});
		setDragMouse({
			x: e.clientX - rect.left,
			y: e.clientY - rect.top,
		});
		e.preventDefault();
	};

	const scrollRafRef = useRef<number | null>(null);
	const mouseClientRef = useRef({ x: 0, y: 0 });

	useEffect(() => {
		if (!dragSource) return;

		const SCROLL_ZONE = 80;
		const MAX_SCROLL_SPEED = 15;

		const updateDragMouseFromClient = () => {
			const rect = dragAreaRef.current?.getBoundingClientRect();
			if (!rect) return;
			setDragMouse({
				x: mouseClientRef.current.x - rect.left,
				y: mouseClientRef.current.y - rect.top,
			});
		};

		const autoScroll = () => {
			const y = mouseClientRef.current.y;
			const vh = window.innerHeight;
			let scrolled = false;

			if (y < SCROLL_ZONE) {
				const speed = Math.round(MAX_SCROLL_SPEED * (1 - y / SCROLL_ZONE));
				window.scrollBy(0, -speed);
				scrolled = true;
			} else if (y > vh - SCROLL_ZONE) {
				const speed = Math.round(MAX_SCROLL_SPEED * (1 - (vh - y) / SCROLL_ZONE));
				window.scrollBy(0, speed);
				scrolled = true;
			}

			if (scrolled) updateDragMouseFromClient();

			scrollRafRef.current = requestAnimationFrame(autoScroll);
		};

		scrollRafRef.current = requestAnimationFrame(autoScroll);

		const handleMove = (e: MouseEvent) => {
			mouseClientRef.current = { x: e.clientX, y: e.clientY };
			updateDragMouseFromClient();
		};

		const handleUp = () => {
			const targetId = hoveredTargetRef.current;
			if (targetId && dragSource) {
				const newItem = newItemsRef.current.find((item) => item.key === dragSource.sourceKey);
				if (newItem) {
					setManualMatches((prev) => [
						...prev,
						{ newKey: dragSource.sourceKey, unmatchedId: targetId },
					]);
					setReferences((prev) => ({ ...prev, [targetId]: newItem.refId }));
				}
			}
			setDragSource(null);
			setDragMouse(null);
			setDragHoverTarget(null);
			hoveredTargetRef.current = null;
		};

		document.addEventListener('mousemove', handleMove);
		document.addEventListener('mouseup', handleUp);
		return () => {
			document.removeEventListener('mousemove', handleMove);
			document.removeEventListener('mouseup', handleUp);
			if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
		};
	}, [dragSource]);

	const handleTargetEnter = (id: string) => {
		if (!dragSource) return;
		hoveredTargetRef.current = id;
		setDragHoverTarget(id);
	};

	const handleTargetLeave = (id: string) => {
		if (hoveredTargetRef.current === id) {
			hoveredTargetRef.current = null;
			setDragHoverTarget(null);
		}
	};

	const description = t('connectors.settings.migration.description', {
		matched: allMatched.length,
		new: displayedNewItems.length,
		archived: displayedUnmatched.length,
	});
	const isEmpty =
		allMatched.length === 0 && displayedNewItems.length === 0 && displayedUnmatched.length === 0;
	const canDrag = displayedUnmatched.length > 0;

	const columnLabelStyle = {
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase' as const,
		letterSpacing: 0.5,
	};

	return (
		<div>
			<Flex alignItems="center" justifyContent="space-between" mb={2}>
				<Box>
					<Heading3>{title}</Heading3>
					<Paragraph>{description}</Paragraph>
				</Box>
				<Button
					type="primary"
					title={
						saving
							? t('connectors.settings.migration.saving')
							: t('connectors.settings.migration.save', { count: changedCount })
					}
					disabled={changedCount === 0 || saving}
					onClick={handleSave}
				/>
			</Flex>

			{loading ? (
				<Spinner centered />
			) : isEmpty ? (
				<Box py={3}>
					<Paragraph textAlign="center">{emptyMessage}</Paragraph>
				</Box>
			) : (
				<Flex flexDirection="column">
					{allMatched.length > 0 && (
						<Accordion
							small
							noPadding
							title={t('connectors.settings.migration.matched', { count: allMatched.length })}
							initialIsCollapsed={false}
							headerRight={
								unsavedMatchedCount > 0 ? (
									<Paragraph
										style={{
											fontSize: 12,
											color: '#007AFF',
											fontWeight: 600,
											flexShrink: 0,
										}}
									>
										<Box mr={3}>
											{t('connectors.settings.migration.unsaved', {
												count: unsavedMatchedCount,
											})}
										</Box>
									</Paragraph>
								) : undefined
							}
						>
							<PairRow style={{ opacity: 0.5, borderBottom: 'none' }}>
								<PairPanel>
									<Paragraph style={columnLabelStyle}>
										{t('connectors.settings.migration.source')}
									</Paragraph>
								</PairPanel>
								<ArrowIndicator />
								<PairPanel>
									<Paragraph style={columnLabelStyle}>
										{t('connectors.settings.migration.current')}
									</Paragraph>
								</PairPanel>
							</PairRow>
							{allMatched.map((pair) => (
								<PairRow key={pair.id} $unsaved={changedIds.has(pair.id)}>
									<PairPanel>
										<ItemLabel name={pair.sourceName} subtitle={pair.sourceSubtitle} />
										<Paragraph
											style={{
												fontSize: 12,
												opacity: 0.5,
												marginTop: 2,
											}}
										>
											Ref: {pair.suggestedRefId || '—'}
										</Paragraph>
									</PairPanel>
									<PairPanel>
										<ItemLabel name={pair.name} subtitle={pair.subtitle} />
										<Box mt={1}>
											<Input
												small
												placeholder={t('connectors.settings.migration.referenceId')}
												value={references[pair.id] ?? ''}
												onChange={(value) =>
													setReferences((prev) => ({
														...prev,
														[pair.id]: value,
													}))
												}
											/>
										</Box>
									</PairPanel>
								</PairRow>
							))}
						</Accordion>
					)}

					{(displayedNewItems.length > 0 || displayedUnmatched.length > 0) && (
						<Accordion
							small
							noPadding
							title={t('connectors.settings.migration.unmatched', {
								count: displayedNewItems.length + displayedUnmatched.length,
							})}
						>
							<DragArea ref={dragAreaRef}>
								<Flex>
									<Box
										flex={1}
										style={{
											backgroundColor: 'rgba(52, 199, 89, 0.06)',
											borderRight: '1px solid rgba(0, 0, 0, 0.06)',
										}}
									>
										<Paragraph style={columnLabelStyle} px={3} pt={2} pb={1}>
											{t('connectors.settings.migration.new', { count: displayedNewItems.length })}
										</Paragraph>
										<Box px={2} pt={1} pb={1}>
											<Input
												small
												placeholder={t('common.search')}
												value={searchNew}
												onChange={setSearchNew}
											/>
										</Box>
										{filteredNewItems.map((item) => (
											<DraggableItem
												key={item.key}
												$isDragging={dragSource?.sourceKey === item.key}
												$canDrag={canDrag}
												onMouseDown={(e) => canDrag && handleDragStart(e, item.key)}
											>
												<ItemLabel name={item.name} subtitle={item.subtitle} />
												<Paragraph
													style={{
														fontSize: 12,
														opacity: 0.5,
														marginTop: 2,
													}}
												>
													Ref: {item.refId}
												</Paragraph>
											</DraggableItem>
										))}
									</Box>
									<Box
										flex={1}
										style={{
											backgroundColor: 'rgba(255, 59, 48, 0.06)',
										}}
									>
										<Paragraph style={columnLabelStyle} px={3} pt={2} pb={1}>
											{t('connectors.settings.migration.willBeArchived', {
												count: displayedUnmatched.length,
											})}
										</Paragraph>
										<Box px={2} pt={1} pb={1}>
											<Input
												small
												placeholder={t('common.search')}
												value={searchArchived}
												onChange={setSearchArchived}
											/>
										</Box>
										{filteredUnmatched.map((row) => (
											<DropTarget
												key={row.id}
												$isHighlighted={dragHoverTarget === row.id}
												onMouseEnter={() => handleTargetEnter(row.id)}
												onMouseLeave={() => handleTargetLeave(row.id)}
											>
												<ItemLabel name={row.name} subtitle={row.subtitle} />
												{row.currentRefId && (
													<Paragraph
														style={{
															fontSize: 12,
															opacity: 0.5,
															marginTop: 2,
														}}
													>
														Ref: {row.currentRefId}
													</Paragraph>
												)}
											</DropTarget>
										))}
									</Box>
								</Flex>

								{dragSource && dragMouse && (
									<svg
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: '100%',
											pointerEvents: 'none',
											zIndex: 10,
										}}
									>
										<line
											x1={dragSource.startX}
											y1={dragSource.startY}
											x2={dragMouse.x}
											y2={dragMouse.y}
											stroke="#5856D6"
											strokeWidth={2}
											strokeDasharray="6 3"
										/>
									</svg>
								)}
							</DragArea>
						</Accordion>
					)}
				</Flex>
			)}

			{saveResult && (
				<>
					<Callout
						color={saveResult.failed > 0 ? 'danger' : 'success'}
						title={
							saveResult.failed > 0
								? t('connectors.settings.migration.saveResultWithFailures', {
										success: saveResult.success,
										failed: saveResult.failed,
									})
								: t('connectors.settings.migration.saveResult', { success: saveResult.success })
						}
					/>
					{saveResult.errors.map((error, index) => (
						<GraphQLErrorMessage key={index} error={error} />
					))}
				</>
			)}
		</div>
	);
}
